import { ReactNode } from 'react';
import clsx from 'clsx';
import { useHistory, useLocation } from 'react-router-dom';

interface SidebarNavButtonProps {
  label: string;
  link?: string;
  active?: boolean | undefined;
  subtitle?: string;
  className?: string;
  iconLeft?: ReactNode;
  onClick?: () => void;
  iconRight?: ReactNode;
  preserveId?: boolean; // Flag to indicate if this nav should preserve last viewed ID
}

export const SidebarNavButton = ({
  label,
  link,
  onClick,
  iconLeft,
  subtitle,
  className,
  iconRight,
  active,
  preserveId = false,
}: SidebarNavButtonProps) => {
  const history = useHistory();
  const location = useLocation();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (link) {
      // If preserveId is true and we're already on this route with an ID, keep it
      if (preserveId && location.pathname.startsWith(link) && location.pathname !== link) {
        // Already on the route with an ID, do nothing
        return;
      }

      // Check if there's a stored last-viewed ID for this route
      if (preserveId) {
        const routeName = link.replace('/', '');
        const storageKey = `${routeName}:lastViewedId`;
        try {
          const lastViewedId = window.localStorage.getItem(storageKey);
          if (lastViewedId) {
            history.push(`${link}/${lastViewedId}`);
            return;
          }
        } catch (error) {
          // Fallback to regular navigation if localStorage fails
          console.warn('Failed to read from localStorage:', error);
        }
      }

      history.push(link);
    }
  };

  // When active prop is explicitly provided, use it directly.
  // Otherwise, derive active state from the current route.
  const isActive =
    active !== undefined
      ? active
      : link &&
        (location.pathname === link ||
          (link !== '/' && location.pathname.startsWith(link + '/')));

  // Repeated-row testid: qualifier is the route slug, not an entity id (story-066).
  // `/dashboard` -> `sidebar-nav-dashboard`, `/` -> `sidebar-nav-root`.
  const navSlug = link
    ? link === '/'
      ? 'root'
      : link.replace(/^\//, '').replace(/\//g, '-')
    : undefined;

  return (
    <button type="button"
      onClick={handleClick}
      data-testid={navSlug ? `sidebar-nav-${navSlug}` : undefined}
      className={clsx(
        'flex items-center justify-between w-full pl-3.5 pr-3 py-2.5 transition-colors duration-200',
        subtitle && 'h-10',
        isActive
          ? 'bg-white/15 dark:bg-gray-700/50 border-r-2 border-cyan-400 dark:border-cyan-300 text-white'
          : 'hover:bg-white/10 dark:hover:bg-gray-700/30 text-white',
        className,
      )}>
      <div className="flex gap-2 items-center">
        {iconLeft && (
          <div className="flex items-center justify-end gap-1.5">{iconLeft}</div>
        )}
        <div className="flex flex-col items-start">
          {/* Sprint 28, story-142. Wrap over truncate for Spanish's longer labels — never
              text-overflow: ellipsis on a nav item (docs/ux/i18n-house-style.md §3). */}
          <span className="font-medium text-white text-sm capitalize leading-tight max-w-[140px]">
            {label}
          </span>
          {subtitle && (
            <span className="text-white/70 text-[11px] truncate max-w-44 capitalize">
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {iconRight && (
        <span className="text-white/80 flex items-center justify-center">
          {iconRight}
        </span>
      )}
    </button>
  );
};
